import '~/content';
import 'maplibre-gl/dist/maplibre-gl.css';

import { AuthConfig, AuthProvider } from '@garner-health/components-auth';
import { GarnerThemeProvider, LogProvider } from '@garner-health/components-common';
import { ContentProvider, init as initContentManager } from '@garner-health/lib-ui-content-management';
import { FeatureFlagProvider } from '@garner-health/lib-ui-feature-flags';
import { trackFeatureFlagEvaluation } from '@garner-health/lib-ui-real-user-monitoring';
import constants from 'app-constants';
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

import { analytics } from '~/analytics/segment';
import * as resourcePackages from '~/content/resource-packages';
import { AppStateProvider } from '~/contexts';
import { ErrorBoundary } from '~/error-boundary';
import { featureFlagsConfig } from '~/feature-flags';
import logger from '~/logging';
import { ErrorPage } from '~/pages';
import { router } from '~/router/router';
import { useComponentConfig } from '~/theme';

import { authTypeResolver } from './auth';
import { initDatadog } from './datadog';

// Initialize Datadog RUM + Logs before anything else so early views and errors are captured.
initDatadog();

const log = logger(__filename);

analytics.initialize();

initContentManager({
  ...constants.contentful,
  log: logger(__filename, { component: 'content-manager' }),
  errorPage: <ErrorPage />,
  applicationStringAppId: 'ui-datapro',
});

// useComponentConfig() depends on the content provider being initialized, so it must be broken into it's own component
const ThemedApp = () => (
  <GarnerThemeProvider locale="en-US" componentConfig={useComponentConfig()}>
    <AppStateProvider>
      <RouterProvider router={router} />
    </AppStateProvider>
  </GarnerThemeProvider>
);

const authConfig: AuthConfig = {
  loginRoute: '/sign-in',
  cognito: {
    ...constants.authConfig.cognito,
    oauth: {
      ...constants.authConfig.cognito.oauth,
      scope: ['openid', 'profile'],
      responseType: 'code',
    },
  },
  log: logger(__filename, { component: 'components-auth' }),
  sso: {
    authTypeResolver,
  },
};

createRoot(document.getElementById('approot')!).render(
  <StrictMode>
    <FeatureFlagProvider config={featureFlagsConfig} onFlagUsed={trackFeatureFlagEvaluation}>
      <AuthProvider config={authConfig}>
        <ErrorBoundary>
          <ContentProvider packages={Array.from(Object.keys(resourcePackages)) as (keyof typeof resourcePackages)[]}>
            <LogProvider log={log}>
              <ThemedApp />
            </LogProvider>
          </ContentProvider>
        </ErrorBoundary>
      </AuthProvider>
    </FeatureFlagProvider>
  </StrictMode>,
);
